import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { Masked, MaskedTeam } from "work/masked-fields";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** One person a proposal's rate may name without adding anybody to the team. */
export type FeeScheduleTeamRosterEntry = {
  readonly member: EntityMember;
  readonly isLeader: boolean;
  /** On the team only as a change one side asked for and the other has not accepted yet. */
  readonly isProposed: boolean;
};

type FeeScheduleTeamRosterParams = {
  entries?: readonly FeeScheduleTeamRosterEntry[];
  isMasked?: boolean;
  /** The legal entity the proposal's client team works for, where this viewer can read it. */
  clientLegalEntityId?: Guid;
};

/**
 * The people a proposal's fee schedule may name: its vendor team as the viewer sees it right now
 * (#5798).
 *
 * `WorkAgreement.ValidateFeeSchedule` refuses to hire while an hourly rate names somebody off the
 * vendor team, so the Team Member field offers this list first and puts everybody else behind
 * "Browse". The leader is included, and so is anybody added since the last save. On an active
 * proposal a member one side proposed and the other has not accepted is included too, marked
 * `isProposed`. Somebody who left the team is not.
 *
 * A masked roster is a team this viewer may not read. It offers nobody, and nobody may be named
 * from it.
 *
 * Nobody from the client's own firm bills a rate either (#6091): the rate is what the client pays the
 * vendor for. So the roster names the client's legal entity as `excludedLegalEntityId`, leaves its
 * people out of `offeredEntries`, and the browse picker leaves them out too. The exception is a
 * proposal whose vendor leads from that same legal entity, where the firm is on both sides and
 * leaving it out would leave out the vendor.
 *
 * A fee schedule template has no team, and so no roster: its callers pass none.
 */
export default class FeeScheduleTeamRoster {
  public static fromVendorTeam(
    team: VendorTeam | MaskedTeam | null | undefined,
    clientTeam?: ClientTeam | MaskedTeam | null,
  ): FeeScheduleTeamRoster {
    if (Masked.isMasked(team)) return FeeScheduleTeamRoster.masked();
    const clientLegalEntityId = FeeScheduleTeamRoster.clientLegalEntityIdOf(clientTeam);
    if (!team) return new FeeScheduleTeamRoster({ clientLegalEntityId });
    return new FeeScheduleTeamRoster({
      entries: [
        ...(team.leader ? [{ member: team.leader, isLeader: true, isProposed: false }] : []),
        ...team.members.map((member) => ({ member, isLeader: false, isProposed: false })),
      ],
      clientLegalEntityId,
    });
  }

  /** The client team's legal entity, or undefined where there is no team or it is masked to this viewer. */
  public static clientLegalEntityIdOf(clientTeam: ClientTeam | MaskedTeam | null | undefined): Guid | undefined {
    if (!clientTeam || Masked.isMasked(clientTeam)) return undefined;
    return clientTeam.leader?.legalEntityId;
  }

  public static masked(): FeeScheduleTeamRoster {
    return new FeeScheduleTeamRoster({ isMasked: true });
  }

  public readonly entries: readonly FeeScheduleTeamRosterEntry[];
  public readonly isMasked: boolean;
  /** Whose people a rate may not name, because they are the client's own: see the class comment. */
  public readonly excludedLegalEntityId?: Guid;

  public get members(): EntityMember[] {
    return this.entries.map((entry) => entry.member);
  }

  /** The entries the Team Member field offers: everybody but the client's own people. */
  public get offeredEntries(): readonly FeeScheduleTeamRosterEntry[] {
    return this.entries.filter((entry) => !this.isExcluded(entry.member));
  }

  /**
   * Each person once, the leader first. A person who leads the team and also sits in its member
   * list is listed as its leader.
   */
  public constructor(params: FeeScheduleTeamRosterParams) {
    const entries: FeeScheduleTeamRosterEntry[] = [];
    const ordered = [...(params.entries ?? [])].sort((a, b) => Number(b.isLeader) - Number(a.isLeader));
    for (const entry of ordered) {
      if (!entry.member.userId?.value || !entry.member.legalEntityId?.value) continue;
      if (entries.some((seen) => seen.member.isEqualTo(entry.member))) continue;
      entries.push(entry);
    }
    this.isMasked = params.isMasked ?? false;
    this.entries = this.isMasked ? [] : entries;
    const leaderLegalEntityId = entries.find((entry) => entry.isLeader)?.member.legalEntityId;
    this.excludedLegalEntityId = params.clientLegalEntityId?.isEqualTo(leaderLegalEntityId)
      ? undefined
      : params.clientLegalEntityId;
  }

  /** Whether this person works for the client, and so may not bill one of its rates. */
  public isExcluded(member: EntityMember | null | undefined): boolean {
    return Boolean(member && this.excludedLegalEntityId?.isEqualTo(member.legalEntityId));
  }

  public includes(member: EntityMember | null | undefined): boolean {
    if (!member) return false;
    return this.entries.some((entry) => entry.member.isEqualTo(member));
  }

  /**
   * Whether a rate naming this person is flagged: somebody is named, this viewer can read the team,
   * and the team does not have them. Such a rate is flagged rather than changed (#5798).
   */
  public isOffTeam(member: EntityMember | null | undefined): boolean {
    return !this.isMasked && Boolean(member) && !this.includes(member);
  }

  /**
   * The person a saved rate names who must join the team, or undefined when nobody must.
   *
   * The field offers only this roster, so a person off it was chosen through "Browse for new
   * member", and that choice means "add them to the team and to this rate" (#5798). The exception is
   * a rate that already named them when the editor opened: it is flagged, not fixed, so saving it
   * unchanged adds nobody. A masked roster adds nobody either, because this viewer cannot change it.
   */
  public newcomerNamedBy(
    teamMember: EntityMember | null | undefined,
    previousTeamMember: EntityMember | null | undefined,
  ): EntityMember | undefined {
    if (this.isMasked || !teamMember) return undefined;
    if (this.includes(teamMember) || teamMember.isEqualTo(previousTeamMember)) return undefined;
    return teamMember;
  }
}
